import { useRouter } from 'expo-router';
import fetchResetPassword, {
  ResetPasswordData,
} from 'features/auth/resetPassword/utils/fetchResetPassword/fetchResetPassword';
import { InformationScreenTypeEnum } from 'features/otp/information/InformationScreenTypeEnum';
import { useContext, useState } from 'react';
import { FeatureFlagKey } from 'services/statsig/types';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import { saveRefreshTokenForReactNative } from 'shared/utils/biometricAuth/biometricAuth';
import { getClientErrorResponse } from 'shared/utils/errors/getMaxLimitErrorResponse';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

const LEGACY_RESET_PASSWORD_PATH = '/reset-password';
const K8S_RESET_PASSWORD_EMAIL_PATH = '/reset-password';
const K8S_RESET_PASSWORD_CHALLENGE_PATH = '/challenge/reset-password';

interface DialogContent {
  title: string;
  message: string;
}

function getResetPasswordPath(
  isK8sAuthServiceEnabled: boolean,
  identityId?: string,
) {
  if (!isK8sAuthServiceEnabled) {
    return LEGACY_RESET_PASSWORD_PATH;
  }
  return identityId
    ? K8S_RESET_PASSWORD_CHALLENGE_PATH
    : K8S_RESET_PASSWORD_EMAIL_PATH;
}

export function useResetPassword() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [dialogContent, setDialogContent] = useState<DialogContent | undefined>(
    undefined,
  );

  const { getVaroSessionId, idToken, storeIdToken, fetchIovationToken } =
    useContext<AuthContextProps>(AuthContext);
  const { currentEnv, getLambdaUrl, getV2AuthUrl } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const { isFeatureEnabled } = useStatsig();

  const isK8sAuthServiceEnabled = isFeatureEnabled(
    FeatureFlagKey.RESET_PASSWORD_USE_K8S_AUTH_SERVICE,
  );
  const authUrl = isK8sAuthServiceEnabled
    ? getV2AuthUrl(currentEnv)
    : getLambdaUrl(currentEnv);

  const resetPassword = async (data: ResetPasswordData) => {
    setDialogContent(undefined);
    setLoading(true);

    try {
      const aToken = await fetchIovationToken();
      const varoSessionId = await getVaroSessionId();
      const headers = await getLambdaHeaders(idToken, varoSessionId);

      const path = getResetPasswordPath(
        isK8sAuthServiceEnabled,
        data.identityId,
      );

      const {
        idToken: responseIdToken = null,
        refreshToken: responseRefreshToken = null,
        errorCode = '',
        title = '',
        message = '',
      } = await fetchResetPassword(
        `${authUrl}${path}`,
        {
          ...data,
          aToken,
        },
        headers,
      );

      if (errorCode) {
        setDialogContent({ title: title, message: message });
        return;
      }

      if (responseRefreshToken !== null) {
        await saveRefreshTokenForReactNative(responseRefreshToken);
      }

      if (responseIdToken === null) {
        router.navigate({
          pathname: '/otp/error',
          params: { screenType: InformationScreenTypeEnum.Error },
        });
      } else {
        await storeIdToken(responseIdToken);

        router.navigate({
          pathname: '/reset-password/success',
        });
      }
    } catch (error) {
      const resetPasswordError = getClientErrorResponse(error, 19);

      if (resetPasswordError) {
        setDialogContent({
          title: resetPasswordError.title ?? '',
          message: resetPasswordError.message,
        });
        return;
      }

      logErrorWithContext('ResetPassword error: ' + error, {
        featureArea: FeatureArea.AUTHENTICATION,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.CRITICAL,
      });

      router.navigate({
        pathname: '/otp/error',
        params: { screenType: InformationScreenTypeEnum.Error },
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    resetPassword,
    loading,
    dialogContent,
    setDialogContent,
  };
}
